/**
 * ResearchBriefInputForm — input gate before Phase 1 brief composition.
 *
 * Renders BEFORE the "Compose brief" button fires so the user sees:
 *   • What's auto-included from the topic context (read-only preview chips)
 *   • What's auto-included from the project (domain, audience summary, tone)
 *   • Optional entity selection via CompositeDataPicker — same stack as
 *     GenerateTopicsDialog / StrategyFormDialog / BlockFormDialog
 *   • Free-text "Additional context" field that becomes `inputs.userHints`
 *
 * Submits via `onCompose` with { userHints, selectedPaths, hydratedData }.
 * Parent (ResearchTab) forwards these to the backend endpoint which puts
 * them on the envelope's `run.inputs.*` before Phase 1 runs.
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Chip,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { SAccordion } from '@modules/ui-components';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { CompositeDataPicker } from '@modules/data-pickers';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { useCompositeDataOrchestrator } from '@modules/strategy-blocks/hooks/useCompositeDataOrchestrator';
import type { ContentTopic } from '../../types';

export interface ResearchBriefInputFormProps {
  projectId: string;
  topic: ContentTopic;
  themeNamesById?: Record<string, string>;
  /** Short project preview — ideally { domain, audienceSummary, targetCountries }. */
  projectPreview?: {
    name?: string;
    domain?: string;
    audienceSummary?: string;
    targetCountries?: string[];
  };
  /** Busy state forwarded from parent (true while Phase 1 is starting). */
  busy?: boolean;
  /** Whether a brief already exists — toggles button label to "Recompose". */
  briefExists?: boolean;
  onCompose: (payload: {
    userHints?: string;
    selectedPaths: string[];
    hydratedData: unknown;
  }) => Promise<void> | void;
}

export const ResearchBriefInputForm: React.FC<ResearchBriefInputFormProps> = ({
  projectId,
  topic,
  themeNamesById,
  projectPreview,
  busy,
  briefExists,
  onCompose,
}) => {
  const [userHints, setUserHints] = useState('');
  const [selectedPaths, setSelectedPaths] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Matches the exact stack used by GenerateTopicsDialog. The strategy service
  // is instantiated inline because Research runs topic-level (no owning
  // strategy); CompositeDataPicker does not actually require strategy state.
  const strategyService = useMemo(() => new StrategyService(projectId), [projectId]);
  const orchestrator = useCompositeDataOrchestrator({
    projectId,
    strategyService,
    initialSelection: selectedPaths,
    selectAll: false,
    onSelectionChange: (d) => setSelectedPaths(d.selectedIds),
    open: true,
  });

  const themes = ((topic as any).themeAssignment ?? []) as Array<{ themeId: string; weight?: number }>;
  const tags = ((topic as any).tags ?? []) as string[];

  const handleCompose = async () => {
    console.log('[ResearchBriefInputForm] Compose button clicked', {
      userHints: userHints.trim() || undefined,
      selectedPathsCount: selectedPaths.length,
      hasHydratedData: !!orchestrator.hydratedData,
    });
    setError(null);
    try {
      await onCompose({
        userHints: userHints.trim() || undefined,
        selectedPaths,
        hydratedData: orchestrator.hydratedData,
      });
      console.log('[ResearchBriefInputForm] onCompose resolved');
    } catch (e) {
      console.error('[ResearchBriefInputForm] onCompose threw', e);
      setError((e as Error).message);
    }
  };

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      {/* ── Auto-included (read-only preview) ───────────────────────── */}
      <SAccordion title="Auto-included from this topic + project" defaultExpanded collapsible>
        <Stack spacing={1.5} sx={{ p: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Topic</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {topic.angle || '(no angle)'}
            </Typography>
            {topic.summary && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {topic.summary}
              </Typography>
            )}
          </Box>

          {themes.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Themes ({themes.length})
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {themes.map((t) => (
                  <Chip
                    key={t.themeId}
                    size="small"
                    label={themeNamesById?.[t.themeId] ?? t.themeId}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {tags.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Tags
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {tags.map((t) => (
                  <Chip key={t} size="small" variant="outlined" label={t} />
                ))}
              </Stack>
            </Box>
          )}

          {projectPreview && (
            <Box>
              <Typography variant="caption" color="text.secondary">Project</Typography>
              <Typography variant="body2">
                {projectPreview.name ?? '(unnamed)'}
                {projectPreview.domain ? ` · ${projectPreview.domain}` : ''}
              </Typography>
              {projectPreview.audienceSummary && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  {projectPreview.audienceSummary}
                </Typography>
              )}
              {projectPreview.targetCountries && projectPreview.targetCountries.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Target countries: {projectPreview.targetCountries.join(', ')}
                </Typography>
              )}
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
            The backend also pulls this topic's target audiences, actors' JTBD,
            recent tasks, big numbers, study cases and the latest insights
            analysis. Use the pickers below to refine the selection.
          </Typography>
        </Stack>
      </SAccordion>

      {/* ── Optional manual selection via CompositeDataPicker ──────── */}
      <SAccordion
        title="Refine data selection (optional)"
        collapsible
        defaultExpanded={false}
      >
        <Box sx={{ p: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            By default the backend auto-resolves entities from the topic. Pick
            specific audiences / tasks / social proofs / insights here to
            override and focus the research.
          </Typography>
          <CompositeDataPicker
            {...orchestrator.compositeProps}
            pickers={[
              { type: 'audience' as const, label: 'Audiences', enabled: true, selectAll: false },
              { type: 'task' as const, label: 'Tasks', enabled: true, selectAll: false },
              { type: 'socialproof' as const, label: 'Social Proof', enabled: true, selectAll: false },
              { type: 'insights' as const, label: 'Insights', enabled: true, selectAll: false },
            ]}
          />
          {selectedPaths.length > 0 && (
            <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 0.5 }}>
              {selectedPaths.length} item(s) manually selected
            </Typography>
          )}
        </Box>
      </SAccordion>

      {/* ── Free-text hints ─────────────────────────────────────────── */}
      <TextField
        label="Additional context (optional)"
        placeholder={
          'e.g. "Focus on SMB tier", "compare against Notion AI", "avoid academic jargon".'
        }
        multiline
        minRows={2}
        maxRows={5}
        value={userHints}
        onChange={(e) => setUserHints(e.target.value)}
        data-testid="research-brief-user-hints"
      />

      {/* ── Submit ──────────────────────────────────────────────────── */}
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={handleCompose}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
          data-testid="research-brief-compose"
        >
          {busy ? 'Starting…' : briefExists ? 'Recompose brief' : 'Compose brief'}
        </Button>
      </Stack>
    </Stack>
  );
};

export default ResearchBriefInputForm;
